// src/components/HoursRow.jsx
// Jedan red = jedna osoba, 7 pod-redaka (PON-NED) s Glovo/Wolt/Ukupno satima po danu

import React, { useState, useCallback } from 'react';
import { db } from '../config/firebase';
import { collection, addDoc, updateDoc, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import Modal from 'react-modal';
import { FaEdit } from 'react-icons/fa';
import ExcelUploader from './ExcelUploader';
import PersonEditModal from './PersonEditModal';
import {
  partsFromDecimalHours,
  formatDecimalHoursDisplay,
  formatDecimalHoursSigned,
} from '../utils/durationUtils';
import '../css/HoursRow.css';

const toDecimalHours = (hours, minutes) => {
  const h = parseInt(hours, 10) || 0;
  const m = Math.min(59, Math.max(0, parseInt(minutes, 10) || 0));
  return h + m / 60;
};

/** Ime u max 2 retka: redak 1 = ime, redak 2 = prezime. Ako stane u 1, jedan redak. */
function formatNameForDisplay(name) {
  if (!name || typeof name !== 'string') return name || '';
  const trimmed = name.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return trimmed;
  const prezime = parts.pop();
  const ime = parts.join(' ');
  return (
    <>
      <span className="hours-name-line1">{ime}</span>
      <span className="hours-name-line2">{prezime}</span>
    </>
  );
}

function HoursRow({
  person,
  dailyMetricsData = [],
  metricsByUserDate = null,
  holidaysByDate = {},
  weeks = [],
  refreshData,
  hiddenColumns = {},
  onExcelUpload,
  selectedFirm = '',
  firms = [],
  onUpdatePerson,
  onArchivePerson,
}) {
  const [workModalOpen, setWorkModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editPersonModalOpen, setEditPersonModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(null);
  const [currentPlatform, setCurrentPlatform] = useState(null);
  const [workDetails, setWorkDetails] = useState({
    glovoHours: 0,
    glovoMinutes: 0,
    woltHours: 0,
    woltMinutes: 0,
  });

  const getMetricForDate = useCallback(
    (year, month, day) => {
      const m = metricsByUserDate
        ? metricsByUserDate.get(`${person.id}_${year}_${month}_${day}`)
        : dailyMetricsData.find(
            (d) =>
              d.userId === person.id &&
              d.year === year &&
              d.month === month &&
              d.day === day
          );
      if (!m) return null;
      const glovoH =
        m.glovoHours ?? (m.actualWorkingHours ? parseFloat(m.actualWorkingHours) : 0);
      const woltH =
        m.woltHours ?? (m.plannedWorkingHours ? parseFloat(m.plannedWorkingHours) : 0);
      return {
        glovoHours: parseFloat(glovoH) || 0,
        woltHours: parseFloat(woltH) || 0,
        totalHours: (parseFloat(glovoH) || 0) + (parseFloat(woltH) || 0),
      };
    },
    [metricsByUserDate, dailyMetricsData, person.id]
  );

  const openWorkModal = (dateInfo, platform) => {
    const { year, month, day } = dateInfo;
    const metric = metricsByUserDate
      ? metricsByUserDate.get(`${person.id}_${year}_${month}_${day}`)
      : dailyMetricsData.find(
          (d) =>
            d.userId === person.id &&
            d.year === year &&
            d.month === month &&
            d.day === day
        );
    setCurrentDate({ year, month, day, ...dateInfo });
    setCurrentPlatform(platform);
    const glovoH =
      metric?.glovoHours ?? (metric?.actualWorkingHours ? parseFloat(metric.actualWorkingHours) : 0);
    const woltH =
      metric?.woltHours ?? (metric?.plannedWorkingHours ? parseFloat(metric.plannedWorkingHours) : 0);
    const glovo = partsFromDecimalHours(glovoH);
    const wolt = partsFromDecimalHours(woltH);
    setWorkDetails({
      glovoHours: glovo.hours,
      glovoMinutes: glovo.minutes,
      woltHours: wolt.hours,
      woltMinutes: wolt.minutes,
    });
    setWorkModalOpen(true);
  };

  const closeWorkModal = () => {
    setWorkModalOpen(false);
    setDeleteConfirmOpen(false);
    setCurrentDate(null);
    setCurrentPlatform(null);
    setWorkDetails({ glovoHours: 0, glovoMinutes: 0, woltHours: 0, woltMinutes: 0 });
  };

  const handleDeleteWorkDetails = async () => {
    if (!currentDate) return;
    try {
      const q = query(
        collection(db, 'dailyMetrics'),
        where('userId', '==', person.id),
        where('year', '==', currentDate.year),
        where('month', '==', currentDate.month),
        where('day', '==', currentDate.day)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        setDeleteConfirmOpen(false);
        closeWorkModal();
        return;
      }
      await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
      setDeleteConfirmOpen(false);
      closeWorkModal();
      refreshData();
    } catch (e) {
      console.error('Greška pri brisanju sati:', e);
      alert('Došlo je do greške prilikom brisanja sati.');
    }
  };

  const handleSaveWorkDetails = async () => {
    if (!currentDate) return;
    const metric = dailyMetricsData.find(
      (d) =>
        d.year === currentDate.year &&
        d.month === currentDate.month &&
        d.day === currentDate.day
    );
    const existingGlovo =
      metric?.glovoHours ?? (metric?.actualWorkingHours ? parseFloat(metric.actualWorkingHours) : 0);
    const existingWolt =
      metric?.woltHours ?? (metric?.plannedWorkingHours ? parseFloat(metric.plannedWorkingHours) : 0);

    const glovoH = toDecimalHours(workDetails.glovoHours, workDetails.glovoMinutes);
    const woltH = toDecimalHours(workDetails.woltHours, workDetails.woltMinutes);

    const updateData =
      currentPlatform === 'glovo'
        ? { glovoHours: glovoH, woltHours: existingWolt }
        : { glovoHours: existingGlovo, woltHours: woltH };

    try {
      const metricsCollection = collection(db, 'dailyMetrics');
      const q = query(
        metricsCollection,
        where('userId', '==', person.id),
        where('year', '==', currentDate.year),
        where('month', '==', currentDate.month),
        where('day', '==', currentDate.day)
      );
      const querySnapshot = await getDocs(q);

      const authUid = person.authId || person.authUid;
      if (authUid) updateData.authUid = authUid;
      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, updateData);
      } else {
        const newMetric = {
          userId: person.id,
          year: currentDate.year,
          month: currentDate.month,
          day: currentDate.day,
          amount: 0,
          glovoHours: currentPlatform === 'glovo' ? glovoH : 0,
          woltHours: currentPlatform === 'wolt' ? woltH : 0,
        };
        if (authUid) newMetric.authUid = authUid;
        await addDoc(metricsCollection, newMetric);
      }

      closeWorkModal();
      refreshData();
    } catch (e) {
      console.error('Greška pri spremanju sati:', e);
      alert('Došlo je do greške prilikom spremanja sati.');
    }
  };

  const DAY_NAMES = ['PON', 'UTO', 'SRI', 'ČET', 'PET', 'SUB', 'NED'];
  const HOURS_PER_WEEK = 50;

  const getHolidayName = (year, month, day) => {
    const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return holidaysByDate[key] || '';
  };

  const getWeekTotal = (weekIndex) => {
    let total = 0;
    const week = weeks[weekIndex];
    if (!week) return 0;
    week.dates.forEach((dateInfo) => {
      const data = getMetricForDate(dateInfo.year, dateInfo.month, dateInfo.day);
      if (data) total += data.totalHours;
    });
    return total;
  };

  const renderDayRows = () => {
    return DAY_NAMES.map((dayName, dayIndex) => (
      <tr key={dayName} className="hours-day-row">
        {dayIndex === 0 ? (
          <td rowSpan={8} className="hours-name-cell">
            <span className="hours-name-content">
              {formatNameForDisplay(person.name)}
            </span>
          </td>
        ) : null}
        {!hiddenColumns.actionsColumn && dayIndex === 0 ? (
          <td rowSpan={8} className="hours-actions-cell">
            <div className="hours-row-actions">
              {onUpdatePerson && onArchivePerson && (
                <>
                  <button
                    type="button"
                    className="hours-action-btn"
                    onClick={() => setEditPersonModalOpen(true)}
                    title="Uredi osobu"
                  >
                    <FaEdit />
                  </button>
                </>
              )}
            </div>
          </td>
        ) : null}
        <td className="hours-day-cell">{dayName}</td>
        {weeks.map((week) => {
          const dateInfo = week.dates[dayIndex];
          if (!dateInfo) {
            return (
              <React.Fragment key={week.weekIndex}>
                {!hiddenColumns.datumColumn && <td className="hours-datum-cell">–</td>}
                {!hiddenColumns.glovoSati && <td className="hours-glovo-cell">–</td>}
                {!hiddenColumns.woltSati && <td className="hours-wolt-cell">–</td>}
                {!hiddenColumns.ukupnoSati && (
                <td className={`hours-ukupno-cell ${dayName === 'NED' ? 'hours-ukupno-ned' : ''}`}>–</td>
              )}
              </React.Fragment>
            );
          }
          const data = getMetricForDate(dateInfo.year, dateInfo.month, dateInfo.day);
          const glovoH = data ? data.glovoHours : 0;
          const woltH = data ? data.woltHours : 0;
          const totalH = data ? data.totalHours : 0;
          const holidayName = getHolidayName(dateInfo.year, dateInfo.month, dateInfo.day);
          const isHoliday = Boolean(holidayName);
          const datumStr = `${dateInfo.dayName} (${dateInfo.day}.${dateInfo.month}.)`;
          return (
            <React.Fragment key={week.weekIndex}>
              {!hiddenColumns.datumColumn && (
                <td
                  className={`hours-datum-cell ${isHoliday ? 'hours-datum-holiday' : ''}`}
                  title={isHoliday ? `${datumStr} - ${holidayName}` : datumStr}
                >
                  <div className="hours-datum-main">{datumStr}</div>
                  {isHoliday ? <div className="hours-holiday-label">{holidayName}</div> : null}
                </td>
              )}
              {!hiddenColumns.glovoSati && (
                <td
                  className="hours-glovo-cell"
                  onClick={() => openWorkModal(dateInfo, 'glovo')}
                  title={`${dateInfo.dayName} ${datumStr} - Glovo: ${formatDecimalHoursDisplay(glovoH)} (klik za uređivanje)`}
                >
                  {glovoH > 0 ? formatDecimalHoursDisplay(glovoH) : '0h'}
                </td>
              )}
              {!hiddenColumns.woltSati && (
                <td
                  className="hours-wolt-cell"
                  onClick={() => openWorkModal(dateInfo, 'wolt')}
                  title={`${dateInfo.dayName} ${datumStr} - Wolt: ${formatDecimalHoursDisplay(woltH)} (klik za uređivanje)`}
                >
                  {woltH > 0 ? formatDecimalHoursDisplay(woltH) : '0h'}
                </td>
              )}
              {!hiddenColumns.ukupnoSati && (
                <td
                  className={`hours-ukupno-cell ${dayName === 'NED' ? 'hours-ukupno-ned' : ''} ${isHoliday ? 'hours-ukupno-holiday' : ''}`}
                  title={`${dateInfo.dayName} ${datumStr} - Ukupno: ${formatDecimalHoursDisplay(totalH)}${isHoliday ? ` - ${holidayName}` : ''}`}
                >
                  <strong>{totalH > 0 ? formatDecimalHoursDisplay(totalH) : '0h'}</strong>
                </td>
              )}
            </React.Fragment>
          );
        })}
      </tr>
    ));
  };

  const renderLeftHoursRow = () => (
    <tr className="hours-left-hours-row">
      <td className="hours-day-cell hours-left-hours-dani">–</td>
      {weeks.map((week) => {
        const weekTotal = getWeekTotal(week.weekIndex);
        const leftHours = HOURS_PER_WEEK - weekTotal;
        return (
          <React.Fragment key={week.weekIndex}>
            {!hiddenColumns.datumColumn && (
              <td className="hours-datum-cell hours-left-hours-label-cell">Left hours</td>
            )}
            {!hiddenColumns.glovoSati && <td className="hours-left-hours-cell">–</td>}
            {!hiddenColumns.woltSati && <td className="hours-left-hours-cell">–</td>}
            {!hiddenColumns.ukupnoSati && (
              <td className="hours-left-hours-ukupno-cell">
                <strong>{formatDecimalHoursSigned(leftHours)}</strong>
              </td>
            )}
          </React.Fragment>
        );
      })}
    </tr>
  );

  return (
    <>
      {renderDayRows()}
      {renderLeftHoursRow()}
      {editPersonModalOpen && onUpdatePerson && onArchivePerson && (
        <Modal
          isOpen
          onRequestClose={() => setEditPersonModalOpen(false)}
          contentLabel="Uredi osobu"
          className="modal person-edit-modal"
          overlayClassName="overlay"
        >
          <button
            className="close-modal-button"
            onClick={() => setEditPersonModalOpen(false)}
            style={{ position: 'absolute', top: 12, right: 12 }}
          >
            ×
          </button>
          <PersonEditModal
            isOpen
            person={person}
            firms={firms}
            onSave={onUpdatePerson}
            onArchive={onArchivePerson}
            onClose={() => setEditPersonModalOpen(false)}
          />
        </Modal>
      )}
      <Modal
        isOpen={workModalOpen}
        onRequestClose={closeWorkModal}
        contentLabel={currentPlatform === 'glovo' ? 'Glovo sati' : 'Wolt sati'}
        className={`modal hours-cell-modal hours-cell-modal-${currentPlatform || 'glovo'}`}
        overlayClassName="overlay"
      >
        <div className="hours-cell-modal-header">
          <h2>
            {currentPlatform === 'glovo' ? 'Glovo' : 'Wolt'} sati – {currentDate?.day}.{currentDate?.month}.{currentDate?.year}.
          </h2>
          <button type="button" className="hours-cell-modal-close" onClick={closeWorkModal}>
            ×
          </button>
        </div>
        <div className="hours-cell-modal-body">
          {onExcelUpload && currentDate && (
            <section className="hours-cell-upload-section">
              <h3>Učitaj Excel za ovaj dan</h3>
              {!selectedFirm ? (
                <p className="hours-cell-upload-hint hours-cell-upload-warning">
                  Odaberite firmu u filtru prije upload-a.
                </p>
              ) : (
                <p className="hours-cell-upload-hint">
                  Spremit će se osobama firme {firms.find((f) => f.id === selectedFirm)?.name || selectedFirm}.
                </p>
              )}
              <ExcelUploader
                onDataParsed={(data, filename) => {
                  onExcelUpload(currentPlatform, currentDate, data, filename);
                  closeWorkModal();
                }}
              >
                {currentPlatform === 'glovo'
                  ? 'Učitaj Glovo Excel (Rider ID, sum CDT in minutes)'
                  : 'Učitaj Wolt Excel (Id, Total on-task time)'}
              </ExcelUploader>
            </section>
          )}
          <section className="hours-cell-edit-section">
            <h3>Ili uredi ručno za {person.name}</h3>
            <form className="edit-work-details-form">
              {currentPlatform === 'glovo' && (
                <div className="form-group duration-input-group">
                  <label>Glovo sati:</label>
                  <div className="duration-inputs">
                    <input
                      type="number"
                      min="0"
                      max="24"
                      value={workDetails.glovoHours}
                      onChange={(e) =>
                        setWorkDetails({
                          ...workDetails,
                          glovoHours: Math.min(24, Math.max(0, parseInt(e.target.value) || 0)),
                        })
                      }
                    />
                    <span>h</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={workDetails.glovoMinutes}
                      onChange={(e) => {
                        let m = parseInt(e.target.value) || 0;
                        let h = workDetails.glovoHours;
                        if (m >= 60) {
                          h += Math.floor(m / 60);
                          m = m % 60;
                        }
                        setWorkDetails({
                          ...workDetails,
                          glovoHours: Math.min(24, h),
                          glovoMinutes: Math.min(59, Math.max(0, m)),
                        });
                      }}
                    />
                    <span>min</span>
                  </div>
                </div>
              )}
              {currentPlatform === 'wolt' && (
                <div className="form-group duration-input-group">
                  <label>Wolt sati:</label>
                  <div className="duration-inputs">
                    <input
                      type="number"
                      min="0"
                      max="24"
                      value={workDetails.woltHours}
                      onChange={(e) =>
                        setWorkDetails({
                          ...workDetails,
                          woltHours: Math.min(24, Math.max(0, parseInt(e.target.value) || 0)),
                        })
                      }
                    />
                    <span>h</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={workDetails.woltMinutes}
                      onChange={(e) => {
                        let m = parseInt(e.target.value) || 0;
                        let h = workDetails.woltHours;
                        if (m >= 60) {
                          h += Math.floor(m / 60);
                          m = m % 60;
                        }
                        setWorkDetails({
                          ...workDetails,
                          woltHours: Math.min(24, h),
                          woltMinutes: Math.min(59, Math.max(0, m)),
                        });
                      }}
                    />
                    <span>min</span>
                  </div>
                </div>
              )}
              <div className="button-group">
                <button type="button" className="save-button" onClick={handleSaveWorkDetails}>
                  Spremi
                </button>
                <button
                  type="button"
                  className="delete-button"
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  Obriši
                </button>
                <button type="button" className="cancel-button" onClick={closeWorkModal}>
                  Odustani
                </button>
              </div>
            </form>
          </section>
        </div>
      </Modal>
      <Modal
        isOpen={deleteConfirmOpen}
        onRequestClose={() => setDeleteConfirmOpen(false)}
        contentLabel="Potvrda brisanja sati"
        className="modal hours-delete-confirm-modal"
        overlayClassName="overlay"
      >
        <div className="hours-delete-confirm">
          <h3>Obrisati sate za ovaj dan?</h3>
          <p>
            {person.name} – {currentDate?.day}.{currentDate?.month}.{currentDate?.year}
          </p>
          <p>Ova akcija briše sve zapise za taj datum i osobu.</p>
          <div className="button-group">
            <button type="button" className="delete-button" onClick={handleDeleteWorkDetails}>
              Da, obriši
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Odustani
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function hoursRowPropsAreEqual(prev, next) {
  return (
    prev.person.id === next.person.id &&
    prev.person.name === next.person.name &&
    prev.metricsByUserDate === next.metricsByUserDate &&
    prev.holidaysByDate === next.holidaysByDate &&
    prev.weeks === next.weeks &&
    prev.hiddenColumns === next.hiddenColumns &&
    prev.selectedFirm === next.selectedFirm
  );
}

export default React.memo(HoursRow, hoursRowPropsAreEqual);
